import {ValueDispatcher} from '@cascade/core';

/**
 * Cache information for a scene, matching the shape that SceneTrack and
 * other timeline components read from MC Scene objects.
 */
export interface SceneCache {
  firstFrame: number;
  lastFrame: number;
  duration: number;
  transitionDuration: number;
}

/**
 * Lightweight wrapper around a Remotion scene description that satisfies
 * the subset of the MC `Scene` interface the UI actually reads.
 *
 * Used by: useScenes(), useCurrentScene(), SceneTrack, AudioTrack.
 */
export class RemotionSceneWrapper {
  public readonly name: string;

  /** Emits cache updates for timeline clip sizing. */
  public get onCacheChanged() {
    return this._cache.subscribable;
  }
  private readonly _cache: ValueDispatcher<SceneCache>;

  /** Sounds subscribable (always empty -- Remotion audio is project-level). */
  public readonly sounds = {
    onChanged: new ValueDispatcher<never[]>([]).subscribable,
    getSounds: () => [] as never[],
  };

  /** No MC creation stack for Remotion scenes. */
  public readonly creationStack: string | undefined = undefined;

  // Remotion-specific data
  public readonly fps: number;
  public readonly durationInFrames: number;
  public readonly component: () => Promise<{default: React.ComponentType}>;

  constructor(opts: {
    name: string;
    fps: number;
    durationInFrames: number;
    component: () => Promise<{default: React.ComponentType}>;
    firstFrame?: number;
  }) {
    this.name = opts.name;
    this.fps = opts.fps;
    this.durationInFrames = opts.durationInFrames;
    this.component = opts.component;

    const firstFrame = opts.firstFrame ?? 0;
    this._cache = new ValueDispatcher<SceneCache>({
      firstFrame,
      lastFrame: firstFrame + opts.durationInFrames,
      duration: opts.durationInFrames,
      transitionDuration: 0,
    });
  }

  /** Update the cache when scenes are reordered or recalculated. */
  public updateCache(firstFrame: number) {
    this._cache.current = {
      firstFrame,
      lastFrame: firstFrame + this.durationInFrames,
      duration: this.durationInFrames,
      transitionDuration: 0,
    };
  }

  // ---- Stubs for Scene interface methods the UI might call ----
  public get firstFrame() {
    return this._cache.current.firstFrame;
  }
  public get lastFrame() {
    return this._cache.current.lastFrame;
  }
  public get transitionDuration() {
    return 0;
  }
  public isCached() {
    return true;
  }
  public isFinished() {
    return false;
  }
}
